import {
  mapQueryWorkflowsList,
  queryWorkflowsList,
  createWorkflow as createWorkflowQuery,
  mapCreateWorkflow,
  updateWorkflow as updateWorkflowQuery,
  deleteWorkflow as deleteWorkflowQuery,
  queryWorkflowsTemplate,
  mapDeleteWorkflow,
  mapQueryWorkflowsTemplate,
  mapUpdateWorkflow,
  queryUARWorkflowsTemplate,
  mapQueryUARWorkflowsTemplate,
  queryUarWorkflows,
  mapQueryUarWorkflows
} from './workflowQueries'
import { WorkflowFormData } from './views/createWorkflows/types'
import graphqlService from '../../services/graphqlService'
import { FilterParams } from '../../interfaces'
import { SubAlertActionState } from '../policies/constants'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export const WORKFLOW_DEFAULT_CRON = '0 0 1 * *'

export enum WorkflowType {
  POLICY_NOTIFICATION_AND_APPROVAL = 'POLICY_NOTIFICATION_AND_APPROVAL',
  UAR_WORKFLOW = 'UAR_WORKFLOW'
}

export enum WorkflowBlockType {
  POLICY_UPSERT_NOTIFICATION = 'POLICY_UPSERT_NOTIFICATION',
  POLICY_STATE_NOTIFICATION = 'POLICY_STATE_NOTIFICATION',
  POLICY_ALERT_ASSIGNMENT = 'POLICY_ALERT_ASSIGNMENT',
  POLICY_STATE_APPROVAL = 'POLICY_STATE_APPROVAL',
  POLICY_ACTION_NOTIFICATION_AND_APPROVAL = 'POLICY_ACTION_NOTIFICATION_AND_APPROVAL',

  UAR_ATTESTATION = 'UAR_ATTESTATION',
  UAR_STATE_APPROVAL = 'UAR_STATE_APPROVAL',
  UAR_REPORT_GENERATION = 'UAR_REPORT_GENERATION',
  UAR_SCAN_COMPLETE_NOTIFICATION = 'UAR_SCAN_COMPLETE_NOTIFICATION'
}

export enum WorkflowRecipientGroup {
  DATASOURCE_OWNER = 'DATASOURCE_OWNER'
}

export type WorkflowBlockItemGroup = {
  items: string[]
  title: string
  type?: 'text' | 'tags'
  className?: string
}

export interface WorkflowBlock {
  __typename: string
  id: string
  type: WorkflowBlockType
  enabled?: boolean
  order?: number
}

export interface WorkflowBlockUpsertNotification extends WorkflowBlock {
  recipientGroups?: WorkflowRecipientGroup[]
  additionalRecipients?: string[]
}

export interface WorkflowBlockStateNotification extends WorkflowBlock {
  recipientGroups?: WorkflowRecipientGroup[]
  additionalRecipients?: string[]
  states: SubAlertActionState[]
  cronExpression: string
}

export type WorkflowBlocks = Array<WorkflowBlockUpsertNotification | WorkflowBlockStateNotification>

export type WorkflowItem = {
  flowId: string
  name: string
  type?: WorkflowType
  createdAt?: string
  createdBy?: string
  blocks?: WorkflowFormData
  attachedPolicyCount?: number
}

// Policy flow
export type WorkflowListParams = {
  page?: number
  filters?: FilterParams
}

export const ACTION_WORKFLOW_LIST = 'workflows/list-policies'
export const fetchWorkflowsList = createAsyncThunk(
  ACTION_WORKFLOW_LIST,
  async (params: WorkflowListParams) => {
    const raw = await graphqlService.execute(queryWorkflowsList(params))
    return mapQueryWorkflowsList(raw)
  }
)

export type CreateWorkflowParams = {
  name: string
  data: WorkflowFormData
  type: WorkflowType
}

export const ACTION_CREATE_WORKFLOW = 'workflows/create-workflow'
export const createWorkflow = createAsyncThunk(
  ACTION_CREATE_WORKFLOW,
  async (params: CreateWorkflowParams) => {
    const { name, data, type } = params
    const query = createWorkflowQuery(name, type, data)
    const raw = await graphqlService.execute(query)
    return mapCreateWorkflow(raw)
  }
)

export type UpdateWorkflowParams = {
  name: string
  flowId: string
  data: WorkflowFormData
  type: WorkflowType
}

export const ACTION_UPDATE_WORKFLOW = 'workflows/update-workflow'
export const updateWorkflow = createAsyncThunk(
  ACTION_UPDATE_WORKFLOW,
  async (params: UpdateWorkflowParams) => {
    const { name, flowId, type, data } = params
    const query = updateWorkflowQuery(name, flowId, type, data)
    const raw = await graphqlService.execute(query)
    return mapUpdateWorkflow(raw)
  }
)

export type DeleteWorkflowParams = {
  flowId: string
  clientMutationId: string
}

export const ACTION_DELETE_WORKFLOW = 'workflows/delete-workflow'
export const deleteWorkflow = createAsyncThunk(
  ACTION_DELETE_WORKFLOW,
  async (params: DeleteWorkflowParams, { rejectWithValue }) => {
    try {
      const { flowId, clientMutationId } = params
      const query = deleteWorkflowQuery(flowId, clientMutationId)
      const raw = await graphqlService.execute(query)
      return mapDeleteWorkflow(raw)
    } catch (err: any) {
      return rejectWithValue({ statusMessage: err?.errors?.[0]?.message })
    }
  }
)

export type FetchWorkflowTemplateParams = {
  flowId: string
}

export const ACTION_FETCH_WORKFLOW_TEMPLATE = 'workflows/fetch-workflow-template'
export const fetchWorkflowTemplate = createAsyncThunk(
  ACTION_FETCH_WORKFLOW_TEMPLATE,
  async (params: FetchWorkflowTemplateParams) => {
    const raw = await graphqlService.execute(queryWorkflowsTemplate(params))
    return mapQueryWorkflowsTemplate(raw)
  }
)

export const ACTION_FETCH_UAR_WORKFLOW_TEMPLATE = 'workflows/fetch-uar-workflow-template'
export const fetchUARWorkflowTemplate = createAsyncThunk(
  ACTION_FETCH_UAR_WORKFLOW_TEMPLATE,
  async (params: FetchWorkflowTemplateParams) => {
    const raw = await graphqlService.execute(queryUARWorkflowsTemplate(params))
    return mapQueryUARWorkflowsTemplate(raw)
  }
)

export const ACTION_FETCH_UAR_WORKFLOWS = 'workflows/fetch-uar-workflows'
export const fetchUarWorkflows = createAsyncThunk(ACTION_FETCH_UAR_WORKFLOWS, async () => {
  const raw = await graphqlService.execute(queryUarWorkflows())
  return mapQueryUarWorkflows(raw)
})

export interface WorkflowsState {
  workflows: {
    list: WorkflowItem[]
    total: number
  }
  createWorkflow: {
    loading: boolean
    error: string | null
    flowId: string | null
  }
  updateWorkflow: {
    loading: boolean
    error: string | null
    flowId: string | null
  }
  deleteWorkflow: {
    loading: boolean
    error: string | null
  }
  workflowTemplate: {
    loading: boolean
    error: string | null
    data: WorkflowItem | null
  }
  uarWorkflowTemplate: {
    loading: boolean
    error: string | null
    data: WorkflowItem | null
  }
}

export const initialState: WorkflowsState = {
  workflows: {
    list: [],
    total: 0
  },
  createWorkflow: {
    loading: false,
    error: null,
    flowId: null
  },
  updateWorkflow: {
    loading: false,
    error: null,
    flowId: null
  },
  deleteWorkflow: {
    loading: false,
    error: null
  },
  workflowTemplate: {
    loading: false,
    error: null,
    data: null
  },
  uarWorkflowTemplate: {
    loading: false,
    error: null,
    data: null
  }
}

const workflowsSlice = createSlice({
  name: 'workflows',
  initialState,
  reducers: {
    resetCreateWorkflow: (state) => {
      state.createWorkflow = {
        loading: false,
        error: null,
        flowId: null
      }
    },
    resetUpdateWorkflow: (state) => {
      state.updateWorkflow = {
        loading: false,
        error: null,
        flowId: null
      }
    },
    resetDeleteWorkflow: (state) => {
      state.deleteWorkflow = {
        loading: false,
        error: null
      }
    },
    resetWorkflowTemplate: (state) => {
      state.workflowTemplate = {
        loading: false,
        error: null,
        data: null
      }
    },
    resetUARWorkflowTemplate: (state) => {
      state.uarWorkflowTemplate = {
        loading: false,
        error: null,
        data: null
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkflowsList.fulfilled, (state, action) => {
        state.workflows = action.payload
      })
      .addCase(fetchUarWorkflows.fulfilled, (state, action) => {
        state.workflows = action.payload
      })
      .addCase(createWorkflow.pending, (state) => {
        state.createWorkflow.loading = true
        state.createWorkflow.error = null
        state.createWorkflow.flowId = null
      })
      .addCase(createWorkflow.fulfilled, (state, action) => {
        state.createWorkflow.loading = false
        state.createWorkflow.error = null
        state.createWorkflow.flowId = action.payload.flowId
      })
      .addCase(createWorkflow.rejected, (state, action) => {
        state.createWorkflow.loading = false
        state.createWorkflow.error = action.error.message || 'workflows.create.error'
        state.createWorkflow.flowId = null
      })
      .addCase(updateWorkflow.pending, (state) => {
        state.updateWorkflow.loading = true
        state.updateWorkflow.error = null
        state.updateWorkflow.flowId = null
      })
      .addCase(updateWorkflow.fulfilled, (state, action) => {
        state.updateWorkflow.loading = false
        state.updateWorkflow.error = null
        state.updateWorkflow.flowId = action.payload.flowId
      })
      .addCase(updateWorkflow.rejected, (state, action) => {
        state.updateWorkflow.loading = false
        state.updateWorkflow.error = action.error.message || 'workflows.update.error'
        state.updateWorkflow.flowId = null
      })
      .addCase(deleteWorkflow.pending, (state) => {
        state.deleteWorkflow.loading = true
        state.deleteWorkflow.error = null
      })
      .addCase(deleteWorkflow.fulfilled, (state) => {
        state.deleteWorkflow.loading = false
        state.deleteWorkflow.error = null
      })
      .addCase(deleteWorkflow.rejected, (state, { payload }) => {
        state.deleteWorkflow.loading = false
        state.deleteWorkflow.error = (payload as any)?.statusMessage || ''
      })
      .addCase(fetchWorkflowTemplate.pending, (state) => {
        state.workflowTemplate.loading = true
        state.workflowTemplate.error = null
        state.workflowTemplate.data = null
      })
      .addCase(fetchWorkflowTemplate.fulfilled, (state, action) => {
        state.workflowTemplate.loading = false
        state.workflowTemplate.error = null
        state.workflowTemplate.data = action.payload
      })
      .addCase(fetchWorkflowTemplate.rejected, (state, action) => {
        state.workflowTemplate.loading = false
        state.workflowTemplate.error = action.error.message || 'workflows.fetchTemplate.error'
        state.workflowTemplate.data = null
      })
      .addCase(fetchUARWorkflowTemplate.pending, (state) => {
        state.uarWorkflowTemplate.loading = true
        state.uarWorkflowTemplate.error = null
        state.uarWorkflowTemplate.data = null
      })
      .addCase(fetchUARWorkflowTemplate.fulfilled, (state, action) => {
        state.uarWorkflowTemplate.loading = false
        state.uarWorkflowTemplate.error = null
        state.uarWorkflowTemplate.data = action.payload
      })
      .addCase(fetchUARWorkflowTemplate.rejected, (state, action) => {
        state.uarWorkflowTemplate.loading = false
        state.uarWorkflowTemplate.error =
          action.error.message || 'Failed to fetch UAR workflow template'
        state.uarWorkflowTemplate.data = null
      })
  }
})

export const {
  resetCreateWorkflow,
  resetUpdateWorkflow,
  resetDeleteWorkflow,
  resetWorkflowTemplate,
  resetUARWorkflowTemplate
} = workflowsSlice.actions

export default workflowsSlice.reducer
